import type { LiliPlan } from '@Pages/happyFlowPages/planSelection/types';
import type { RoutesMap } from '@Router/router.types.ts';
import type { Country } from './country';
import type { ConsentTypes } from '@Services/api/termsAndConditions';

export enum AppStateTypes {
  READY = 'READY',
  PRELOAD = 'PRELOAD',
  LOADING = 'LOADING',
}

export enum OnboardingFlowIdentifier {
  DIRECT = 'DIRECT',
  DIRECT_MULTI_BUSINESS = 'MULTI_BUSINESS',
  PARTNER_CONNECT = 'PARTNER_CONNECT',
  PARTNER_EMBEDDED = 'PARTNER_EMBEDDED',
  UBO = 'UBO',
  AUTHORIZED_USER = 'AUTHORIZED_USER',
  AUTHORIZED_USER_ACCOUNTANT_PORTAL = 'AUTHORIZED_USER_ACCOUNTANT_PORTAL',
  ACCOUNTANT_AS_OWNER = 'ACCOUNTANT_AS_OWNER',
  NON_US = 'NON_US',
}

export enum OnboardingFlowType {
  PARTNER = 'PARTNER',
  EMBEDDED = 'EMBEDDED',
  REGULAR = 'REGULAR',
}

export enum AbTestFlowIds {
  CONTROL = 'CONTROL ',
}

export enum DeviceType {
  IOS = 'ios',
  ANDROID = 'android',
  UNKNOWN = 'unknown',
}

export interface RouterData {
  prevRoute: string;
  currentRoute: string;
}
export interface IOnboardingSlice {
  onboardingSlice: {
    isOnboardingLocked: boolean;
    isAppSubmitted: boolean;
    navigationStack: RoutesMap[];
    pickupWhereLeftNavigationStack: RoutesMap[];
    appState: AppStateTypes;
    editMode: boolean;
    currentPageIndex: number;
    progressValue: number;
    flowType: OnboardingFlowType;
    isFromLiliApp: boolean;
    isPartnerFlow: boolean;
    partnerName: string;
    onboardingFlowIdentifier: OnboardingFlowIdentifier;
    abTestFlowId: AbTestFlowIds;
    onboardingStartTime: number;
    deviceType: DeviceType;
    partnerToken: string;
    discountPartnerId: string;
    customerKey: string;
    customerId: string;
    authorizedUserId: string;
    uboId: string;
    emailParam: string;
    multiBusinessId: string;
    referralCode: string;
    sourcePlatform: string;
    adSource: string;
    isLoginProcessFinished: boolean;
    isPlanActivelySelected: boolean;
    routerData: RouterData;
    deviceId: string;
    countries: Country[];
    isAnnualPlanSelected: boolean;
    preSelectedAnnualPlan: boolean;
    selectedPlan?: LiliPlan;
    partnerLabels?: { disclaimer: string; message: string; title: string }[];
    bufferedPoa: {
      type: ConsentTypes | null;
      blob: Blob | null;
    };
    globalCardFirstName: string;
    globalCardLastName: string;
    activeUboFirstName: string;
    activeUboLastName: string;
    isWhiteGloveFlow: boolean;
    variationId?: string;
    loginToken?: string;
    continueMultiBusiness?: boolean;

    /** Setters */
    setQueryParamValue: (key: string, value: string) => void;
    setAppState: (value: AppStateTypes) => void;
    setEditMode: (value: boolean) => void;
    setCurrentPageIndex: (value: number) => void;
    setProgressValue: (value: number) => void;
    setFlowType: (value: OnboardingFlowType) => void;
    setAbTestFlowId: (value: AbTestFlowIds) => void;
    setIsPartnerFlow: (value: boolean) => void;
    setPartnerName: (value: string) => void;
    setOnboardingIdentifier: (value: OnboardingFlowIdentifier) => void;
    setIsLoginProcessFinished: (value: boolean) => void;
    setNavigationStack: (value: RoutesMap[]) => void;
    setPickupWhereLeftNavigationStack: (value: RoutesMap[]) => void;
    setPlanActivelySelected: (value: boolean) => void;
    setRouterData: (value: RouterData) => void;
    setDeviceId: (value: string) => void;
    setPreSelectedAnnualPlan: (value: boolean) => void;
    setIsAnnualPlanSelected: (value: boolean) => void;
    setSelectedPlan: (value: LiliPlan) => void;
    setBufferedPoa: (value: { type: ConsentTypes | null; blob: Blob | null }) => void;
    setIsOnboardingLocked: (value: boolean) => void;
    setIsAppSubmitted: (value: boolean) => void;
    setPartnerLabels: (value: { disclaimer: string; message: string; title: string }[]) => void;
    setGlobalCardName: (firstName: string, lastName: string) => void;
    setActiveUboName: (firstName: string, lastName: string) => void;
    setIsWhiteGloveFlow: (value: boolean) => void;
    setVariationId: (value: string) => void;
    /** Getters */
    getOnboardingData: () => IOnboardingSlice['onboardingSlice'];
    getIsEmbedded: () => boolean;
    getIsWhiteGloveFlow: () => boolean;
    setCountries: (value: Country[]) => void;
  };
}
